import React, { useState } from 'react'
import axios from 'axios'
import { useUser } from '../../store/userState'
import { EyeSlashIcon, EyeIcon } from '@heroicons/react/24/solid'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import neural1 from '../../assets/404neural.webp'
import nexus from '../../assets/nexusLogo.png'
import { Spinner } from '@material-tailwind/react'
import AppFooter from '../../Components/AppFooter'

const LoginPage = () => {
  const navigate = useNavigate()
  const { updateUser } = useUser()
  const [userCredentials, setUserCredentials] = useState({ email: '', password: '' })
  const [isLoad, setIsLoad] = useState(false)
  const [toggleShowPW, setToggleShowPW] = useState(false)

  const handleTogglePW = () => setToggleShowPW((prev) => !prev)

  const handleLogin = (e) => {
    e.preventDefault()
    setIsLoad(true)

    axios
      .post('/api/users/auth/login', userCredentials)
      .then((response) => {
        if (response.data.message) {
          toast.error(`Login failed! ${response.data.message}`)
        } else {
          localStorage.setItem('jwt', response.data.token)
          localStorage.setItem('id', response.data.user.id)
          updateUser(response.data.user)
          navigate(response.data.user.role === 'Admin' ? '/adminNXS0011/dashboard' : '/home')
        }
        setIsLoad(false)
      })
      .catch((error) => {
        console.error('Error:', error)
        setIsLoad(false)
        const errorMessage = error.response?.data?.message || 'An error occurred during login.'
        toast.error(errorMessage)
      })
  }

  return (
    <>
      <main className="relative isolate flex min-h-screen w-full flex-col overflow-hidden bg-night px-4 py-5 sm:px-8 lg:px-12">
        <img
          src={neural1}
          alt=""
          className="absolute inset-0 -z-30 h-full w-full scale-[1.16] object-cover object-[center_68%]"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-20 bg-[linear-gradient(90deg,rgba(9,17,29,0.18)_0%,rgba(9,17,29,0.4)_42%,rgba(9,17,29,0.96)_78%,#09111D_100%)] max-lg:bg-[linear-gradient(180deg,rgba(9,17,29,0.55)_0%,rgba(9,17,29,0.92)_60%,#09111D_100%)]"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_16%_78%,rgba(66,203,229,0.14),transparent_30%)]"
          aria-hidden="true"
        />

        <div className="mx-auto flex w-full max-w-7xl flex-1 items-center justify-between gap-12">
          <section className="hidden max-w-md pb-4 lg:block" aria-label="Portal description">
            <div className="mb-5 h-px w-16 bg-signal" />
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-signal">
              Outdoor Equipped · Nexus V2
            </p>
            <h2 className="mt-3 font-display text-5xl font-semibold leading-[0.95] tracking-wide text-paper">
              Your operations
              <br />
              start here.
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-6 text-paper/70">
              Access department tools, review active batches, and keep daily work moving.
            </p>
          </section>

          <section className="mx-auto w-full max-w-[27rem] rounded-2xl border border-rule/90 bg-surface/95 p-6 shadow-[0_28px_80px_rgba(0,0,0,0.55)] backdrop-blur-md sm:p-9 lg:mx-0">
            <div className="mb-8 flex items-center justify-between gap-4 border-b border-rule pb-5">
              <div className="flex items-center gap-2">
                <div className="h-10 w-44">
                  <img src={nexus} className="h-full w-full object-contain object-left" alt="Nexus V2" />
                </div>
                <span className="rounded-md border border-signal/40 bg-signal/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-signal">
                  V2
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                Secure portal
              </span>
            </div>

            <p className="nx-manifest-kicker">Account access</p>
            <h1 className="mt-2 font-display text-4xl font-semibold tracking-wide text-paper">
              Welcome back
            </h1>
            <p className="mt-1 text-sm text-muted">Enter your work credentials to continue.</p>

            <form onSubmit={handleLogin} className="mt-7 space-y-5">
              <div>
                <label htmlFor="email" className="nx-label">
                  Work email
                </label>
                <input
                  id="email"
                  className="nx-input"
                  type="email"
                  autoComplete="email"
                  required
                  onChange={(e) => setUserCredentials({ ...userCredentials, email: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="password" className="nx-label">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    className="nx-input pr-10"
                    name="password"
                    type={toggleShowPW ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    onChange={(e) =>
                      setUserCredentials({ ...userCredentials, password: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    onClick={handleTogglePW}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-paper"
                    aria-label={toggleShowPW ? 'Hide password' : 'Show password'}
                  >
                    {toggleShowPW ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Link to="/password-recover" className="text-sm text-muted hover:text-signal">
                  Forgot password?
                </Link>
              </div>

              <button className="nx-btn-primary w-full py-2.5" type="submit" disabled={isLoad}>
                  {isLoad ? (
                  <span className="flex items-center gap-2">
                      Signing in
                      <Spinner className="h-4 w-4" color="blue" />
                    </span>
                  ) : (
                    'Sign in'
                  )}
                </button>

              <p className="border-t border-rule pt-4 text-xs leading-5 text-muted">
                By signing in you agree to Nexus V2&apos;s{' '}
                <a
                  className="text-signal hover:underline"
                  target="_blank"
                  rel="noreferrer"
                  href="https://docs.google.com/document/d/1eebN7Q0weU2d_InjqjXLaUMwzS_0vJ93oY5EAjM49JM/edit?tab=t.0#heading=h.pu589vg78r4r"
                >
                  Privacy Policy
                </a>
              </p>
            </form>
          </section>
        </div>
        <AppFooter className="mx-auto w-full max-w-7xl border-rule/70" />
      </main>
    </>
  )
}

export default LoginPage
